"use client";

import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  type SortingState,
  type ColumnDef,
  type RowSelectionState,
  useReactTable,
} from "@tanstack/react-table";
export type { SortingState } from "@tanstack/react-table";
import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import {
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Search,
  Loader2,
} from "lucide-react";
import { useDataTablePagination } from "@/lib/hooks/useDataTablePagination";
import { DataTablePagination } from "./DataTablePagination";
import { Input } from "@/components/forms";
import { GridSearch } from "./search/GridSearch";
import type { GridSearchColumn } from "./search/types";
import { ArchiveSwitch } from "@/components/common/ui/ArchiveSwitch";
import { Typography } from "@/components/common/ui/Typography";
import { cn } from "@/lib/utils/cn";
import { isNestedInteractive } from "@/lib/utils/nested-interactive";
import {
  loadDataTableState,
  saveDataTableState,
} from "@/lib/hooks/usePersistedFilters";

export type DataTablePaginationPlacement =
  | "toolbar"
  | "footer"
  | "toolbar-and-footer";

export interface DataTableProps<TData> {
  columns: ColumnDef<TData>[];
  data: TData[];
  enablePagination?: boolean;
  enableSearch?: boolean;
  enableSorting?: boolean;
  pageSize?: number;
  pageSizeOptions?: number[];
  showSizeChanger?: boolean;
  paginationPlacement?: DataTablePaginationPlacement;
  searchPlaceholder?: string;
  isLoading?: boolean;
  emptyMessage?: React.ReactNode;
  className?: string;
  onRowClick?: (row: TData) => void;
  /**
   * Where the row leads, for the ways a browser opens a link in a new tab.
   *
   * Rows are `<tr onClick>` rather than anchors, so middle-click and ctrl/cmd+click
   * did nothing: React's `onClick` only fires for the primary button, and with no
   * `href` the browser has nothing of its own to open. Supplying this makes both
   * gestures open `href` in a new tab, alongside the normal `onRowClick`.
   *
   * Right-click "Open in new tab" and hover-preview still will not work — those
   * need a real anchor, and wrapping cell content in one would nest anchors inside
   * the per-cell links and buttons these tables already carry.
   */
  rowHref?: (row: TData) => string;
  serverSidePagination?: boolean;
  totalRows?: number;
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  onSearchChange?: (value: string) => void;
  initialSearchValue?: string;
  /**
   * When provided (and non-empty), renders the BAR-227 column-search control —
   * a column picker joined to the search input — instead of the plain search
   * input. Requires server-side search (`onSearchChange`). Omit it and the
   * table renders exactly the current single search input, so existing tables
   * are untouched.
   */
  searchColumns?: GridSearchColumn[];
  /** Selected search column key. Required when `searchColumns` is provided. */
  selectedSearchColumn?: string;
  /** Called when the user picks a different search column. */
  onSearchColumnChange?: (key: string) => void;
  onSortChange?: (sorting: SortingState) => void;
  initialSorting?: SortingState;
  /**
   * Disables TanStack's tri-state "remove sort" step (asc → desc → none), so a
   * header only ever toggles asc↔desc. Opt in for tables that always carry an
   * effective sort — URL-backed grids whose sort falls back to a grid default
   * (e.g. Transportation's "Received"/desc). Without it, the first click on the
   * default-sorted column resolves to "no sort" and silently no-ops the already
   * absent URL params, which reads as broken sorting (TP-455).
   *
   * Leave it off (default) for server tables that have NO default sort and rely
   * on the removal step to return to the backend's natural order (e.g. the
   * market-finder management tabs, which start unsorted).
   */
  disableSortRemoval?: boolean;
  enableRowSelection?: boolean;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: (selection: RowSelectionState) => void;
  getRowId?: (row: TData) => string;
  toolbarExtraLeft?: React.ReactNode;
  toolbarExtraRight?: React.ReactNode;
  toolbarPaginationExtra?: React.ReactNode;
  /**
   * Row count in the toolbar, for grids that carry no pagination control to
   * host it (`enablePagination={false}`) — paginated grids already get
   * "1 to 50 of 123" from `Pagination`'s `showTotal`. `true` renders
   * "N results"; pass a function to word it for the domain.
   */
  showTotalCount?: boolean | ((total: number) => React.ReactNode);
  /** When true, renders an archive toggle button in the toolbar. */
  archivable?: boolean;
  /** Whether archived items are currently being shown. Required when archivable is true. */
  showArchived?: boolean;
  /** Called when the user toggles between active/archived views. Required when archivable is true. */
  onArchiveToggleChange?: (showArchived: boolean) => void;
  hasCards?: boolean;
  /**
   * When provided, persists sorting, pagination (pageIndex + pageSize), and
   * search to sessionStorage under `list-filters:{persistKey}` and hydrates
   * from it on mount. Ignored when `serverSidePagination` is true — server-side
   * tables persist via URL params via `useTableSearchParams`. Recommended
   * value: route pathname (e.g. "/admin/companies").
   */
  persistKey?: string;
}

export function DataTable<TData>({
  columns,
  data,
  enablePagination = true,
  enableSearch = true,
  enableSorting = true,
  pageSize = 25,
  pageSizeOptions = [10, 25, 50, 100],
  showSizeChanger = true,
  paginationPlacement = "toolbar",
  searchPlaceholder = "Search...",
  isLoading = false,
  emptyMessage = "No data available",
  className = "",
  onRowClick,
  rowHref,
  serverSidePagination = false,
  totalRows: serverTotalRows,
  currentPage: serverCurrentPage,
  totalPages: serverTotalPages,
  onPageChange,
  onPageSizeChange,
  onSearchChange,
  initialSearchValue = "",
  searchColumns,
  selectedSearchColumn,
  onSearchColumnChange,
  onSortChange,
  initialSorting = [],
  disableSortRemoval = false,
  enableRowSelection = false,
  rowSelection: controlledRowSelection,
  onRowSelectionChange,
  getRowId,
  toolbarExtraLeft,
  toolbarExtraRight,
  toolbarPaginationExtra,
  showTotalCount = false,
  archivable,
  showArchived,
  onArchiveToggleChange,
  hasCards,
  persistKey,
}: DataTableProps<TData>) {
  const useServerSort = serverSidePagination && !!onSortChange;
  const persistEnabled = !!persistKey && !serverSidePagination;
  const persisted = useMemo(
    () => (persistEnabled ? loadDataTableState(persistKey!) : null),
    [persistEnabled, persistKey],
  );
  if (
    process.env.NODE_ENV !== "production" &&
    persistKey &&
    serverSidePagination
  ) {
    console.warn(
      "[DataTable] persistKey is ignored when serverSidePagination is true. " +
        "Server-side tables persist via URL params (useTableSearchParams).",
    );
  }
  const [sorting, setSorting] = useState<SortingState>(
    () => persisted?.sorting ?? initialSorting,
  );
  const [globalFilter, setGlobalFilter] = useState(
    () => persisted?.search ?? "",
  );
  const [internalRowSelection, setInternalRowSelection] = useState<RowSelectionState>({});
  const rowSelection = controlledRowSelection ?? internalRowSelection;

  const handleRowSelectionChange = useCallback(
    (updater: RowSelectionState | ((old: RowSelectionState) => RowSelectionState)) => {
      const newSelection = typeof updater === "function" ? updater(rowSelection) : updater;
      if (onRowSelectionChange) {
        onRowSelectionChange(newSelection);
      } else {
        setInternalRowSelection(newSelection);
      }
    },
    [rowSelection, onRowSelectionChange],
  );

  const selectionColumn: ColumnDef<TData> = {
    id: "_select",
    header: ({ table }) => (
      <input
        type="checkbox"
        checked={table.getIsAllPageRowsSelected()}
        onChange={table.getToggleAllPageRowsSelectedHandler()}
        className="rounded border-gray-300"
      />
    ),
    cell: ({ row }) => (
      <input
        type="checkbox"
        checked={row.getIsSelected()}
        onChange={row.getToggleSelectedHandler()}
        onClick={(e) => e.stopPropagation()}
        className="rounded border-gray-300"
      />
    ),
    size: 40,
    enableSorting: false,
  };

  const finalColumns = enableRowSelection ? [selectionColumn, ...columns] : columns;

  // Server-side search: local input state + debounced callback
  const useServerSearch = serverSidePagination && !!onSearchChange;
  const [searchInput, setSearchInput] = useState(
    () => persisted?.search ?? initialSearchValue,
  );
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearchInputChange = useCallback(
    (value: string) => {
      setSearchInput(value);
      if (useServerSearch) {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
          debounceRef.current = null;
          onSearchChange!(value);
        }, 400);
      } else {
        setGlobalFilter(value);
      }
    },
    [useServerSearch, onSearchChange],
  );

  // Sync the box to URL-driven search changes after mount (restore on
  // back-nav, browser back/forward); `debounceRef` guard avoids clobbering
  // in-flight typing.
  useEffect(() => {
    if (useServerSearch && !debounceRef.current) {
      setSearchInput(initialSearchValue);
    }
  }, [initialSearchValue, useServerSearch]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const table = useReactTable({
    data,
    columns: finalColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: enableSorting && !useServerSort ? getSortedRowModel() : undefined,
    getFilteredRowModel: enableSearch ? getFilteredRowModel() : undefined,
    getPaginationRowModel:
      enablePagination && !serverSidePagination
        ? getPaginationRowModel()
        : undefined,
    onSortingChange: (updater) => {
      const newSorting = typeof updater === "function" ? updater(sorting) : updater;
      setSorting(newSorting);
      if (useServerSort) {
        onSortChange!(newSorting);
      }
    },
    manualSorting: useServerSort,
    // Pin first sort direction for server-side tables so a leading null value can't flip TanStack's data-dependent auto direction (none → asc → desc → none).
    sortDescFirst: useServerSort ? false : undefined,
    // Disable the tri-state "remove sort" step only for tables that opt in via
    // `disableSortRemoval` — those that always carry an effective sort (URL-backed
    // grids that fall back to a grid default). Clicking a header then toggles
    // asc↔desc and always writes a direction. With removal enabled, the first
    // click on the default-sorted-desc column (e.g. "Received") resolves to "no
    // sort", which deletes the already-absent URL params — a silent no-op the
    // user reads as "sorting is broken" (TP-455). Server tables with no default
    // sort (e.g. market-finder management tabs) leave this off so they can clear
    // the sort and return to the backend's natural order.
    enableSortingRemoval: disableSortRemoval ? false : undefined,
    onGlobalFilterChange: setGlobalFilter,
    enableRowSelection,
    onRowSelectionChange: handleRowSelectionChange,
    getRowId: getRowId ? (originalRow) => getRowId(originalRow) : undefined,
    state: {
      sorting,
      globalFilter,
      rowSelection,
    },
    initialState: {
      pagination: {
        pageIndex: persisted?.pageIndex ?? 0,
        pageSize: persisted?.pageSize ?? pageSize,
      },
    },
    autoResetPageIndex: persistEnabled ? false : undefined,
  });

  const paginationState = useDataTablePagination({
    serverSidePagination,
    pageSize,
    table,
    serverTotalRows,
    serverCurrentPage,
    serverTotalPages,
  });

  const tablePageIndex = table.getState().pagination.pageIndex;
  const tablePageSize = table.getState().pagination.pageSize;
  useEffect(
    function persistDataTableState() {
      if (!persistEnabled) return;
      saveDataTableState(persistKey!, {
        sorting,
        search: globalFilter,
        pageIndex: tablePageIndex,
        pageSize: tablePageSize,
      });
    },
    [
      persistEnabled,
      persistKey,
      sorting,
      globalFilter,
      tablePageIndex,
      tablePageSize,
    ],
  );

  const useColumnSearch =
    useServerSearch && !!searchColumns && searchColumns.length > 0;

  const handleSearchColumnChangeInternal = useCallback(
    (key: string) => {
      // Column change clears the value under the old column's kind: cancel any
      // in-flight debounce and reset the local input before navigating.
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = null;
      setSearchInput("");
      onSearchColumnChange?.(key);
    },
    [onSearchColumnChange],
  );

  const searchBar = enableSearch ? (
    useColumnSearch ? (
      <GridSearch
        columns={searchColumns!}
        selectedColumn={selectedSearchColumn ?? searchColumns![0].key}
        onColumnChange={handleSearchColumnChangeInternal}
        value={searchInput}
        onValueChange={handleSearchInputChange}
      />
    ) : (
      <div className="relative w-full">
        <Input
          value={useServerSearch ? searchInput : (globalFilter ?? "")}
          onChange={(e) => handleSearchInputChange(e.target.value)}
          placeholder={searchPlaceholder}
          inputSize="sm"
          leftIcon={<Search className="w-4 h-4" />}
          clearable
        />
      </div>
    )
  ) : null;

  const shouldShowArchiveToggle =
    archivable && showArchived !== undefined && !!onArchiveToggleChange;

  const archiveToggle = shouldShowArchiveToggle ? (
    <ArchiveSwitch
      checked={showArchived!}
      onChange={onArchiveToggleChange!}
    />
  ) : null;

  const hasFooter = table
    .getAllColumns()
    .some((column) => column.columnDef.footer);

  const showPaginationInToolbar =
    paginationPlacement === "toolbar" ||
    paginationPlacement === "toolbar-and-footer";
  const showPaginationInFooter =
    paginationPlacement === "footer" ||
    paginationPlacement === "toolbar-and-footer";

  function renderPaginationControls() {
    if (
      !enablePagination ||
      isLoading ||
      table.getRowModel().rows.length === 0
    ) {
      return null;
    }

    return (
      <DataTablePagination
        table={table}
        pageSizeOptions={pageSizeOptions}
        showSizeChanger={showSizeChanger}
        serverSidePagination={serverSidePagination}
        {...paginationState}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    );
  }

  // Row count for grids with no pagination control to carry it (MR-469).
  // `paginationState.totalRows` already tracks the client filter, so the count
  // follows the search box instead of reporting the unfiltered dataset.
  const totalCount = showTotalCount && !isLoading ? (
    typeof showTotalCount === "function" ? (
      showTotalCount(paginationState.totalRows)
    ) : (
      <Typography variant="small" color="muted" className="whitespace-nowrap">
        {paginationState.totalRows}{" "}
        {paginationState.totalRows === 1 ? "result" : "results"}
      </Typography>
    )
  ) : null;

  const toolbarPagination = showPaginationInToolbar
    ? renderPaginationControls()
    : null;
  const footerPagination = showPaginationInFooter
    ? renderPaginationControls()
    : null;

  return (
    <div
      className={cn("bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col", hasCards ? "max-h-[calc(100dvh-12.5rem)] [body[data-environment-banner]_&]:max-h-[calc(100dvh-14.25rem)]" : "max-h-[calc(100dvh-8rem)] [body[data-environment-banner]_&]:max-h-[calc(100dvh-9.75rem)]", className)}
    >
      {(enableSearch || archivable || toolbarExtraLeft || toolbarExtraRight || toolbarPaginationExtra || toolbarPagination || totalCount) && (
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-4 shrink-0">
          {(toolbarExtraLeft || toolbarExtraRight || archiveToggle || totalCount) && (
            <div className="flex items-center gap-2">
              {toolbarExtraLeft}
              {toolbarExtraRight}
              {archiveToggle}
              {totalCount}
            </div>
          )}
          <div className="grow">
            {searchBar}
          </div>
          {(toolbarPaginationExtra || toolbarPagination) && (
            <div className="flex items-center gap-2">
              {toolbarPaginationExtra}
              {toolbarPagination}
            </div>
          )}
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 text-navigator-primary animate-spin" />
          </div>
        ) : table.getRowModel().rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            {typeof emptyMessage === "string" ? (
              <Typography variant="body" color="light">{emptyMessage}</Typography>
            ) : (
              emptyMessage
            )}
          </div>
        ) : (
            <table className="w-full">
              <thead className="sticky top-0 z-10">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr
                    key={headerGroup.id}
                    className="bg-[#f2f6f8]"
                  >
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className="text-left py-3.5 px-4"
                      >
                        {header.isPlaceholder ? null : (
                          <Typography
                            variant="small"
                            weight="semibold"
                            color="text-navigator-primary-dark"
                            as="div"
                            className={
                              header.column.getCanSort() && enableSorting
                                ? "flex items-center gap-2 cursor-pointer select-none hover:text-navigator-primary transition-colors uppercase tracking-wider"
                                : "flex items-center gap-2 uppercase tracking-wider"
                            }
                            onClick={
                              enableSorting
                                ? header.column.getToggleSortingHandler()
                                : undefined
                            }
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {header.column.getCanSort() && enableSorting && (
                              <span className="text-navigator-primary/60">
                                {header.column.getIsSorted() === "asc" ? (
                                  <ArrowUp className="w-3.5 h-3.5" />
                                ) : header.column.getIsSorted() === "desc" ? (
                                  <ArrowDown className="w-3.5 h-3.5" />
                                ) : (
                                  <ArrowUpDown className="w-3.5 h-3.5" />
                                )}
                              </span>
                            )}
                          </Typography>
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row, index) => (
                  <tr
                    key={row.id}
                    onClick={(e) => {
                      // Ctrl/cmd+click is the other "new tab" gesture, and it DOES
                      // reach onClick — so it has to be caught here or it would
                      // navigate the current tab instead.
                      //
                      // The guard is deliberately inside this branch and not above
                      // it: the plain onRowClick path stays byte-for-byte what it
                      // was, so no other table in the app changes behaviour.
                      const href = rowHref?.(row.original);
                      if (
                        href &&
                        (e.metaKey || e.ctrlKey) &&
                        !isNestedInteractive(e.target)
                      ) {
                        window.open(href, "_blank", "noopener,noreferrer");
                        return;
                      }
                      onRowClick?.(row.original);
                    }}
                    // IMPORTANT: this preventDefault is why the middle button works
                    // at all, and it has to be on MOUSEDOWN. Chrome starts autoscroll
                    // on a middle mousedown, and the mouseup that follows is consumed
                    // by leaving autoscroll — so `auxclick` never fires and an
                    // `e.preventDefault()` inside it is far too late. Suppressing
                    // autoscroll here lets auxclick arrive normally.
                    //
                    // Nested controls are left completely alone: an anchor's own
                    // middle-click opening is the browser's default and works without
                    // our help.
                    onMouseDown={(e) => {
                      if (e.button !== 1) return;
                      if (!rowHref) return;
                      if (isNestedInteractive(e.target)) return;
                      e.preventDefault();
                    }}
                    // Middle-click never reaches onClick: React only wires that to
                    // the primary button. `auxclick` is where button 1 lands.
                    onAuxClick={(e) => {
                      if (e.button !== 1) return;
                      if (isNestedInteractive(e.target)) return;
                      const href = rowHref?.(row.original);
                      if (!href) return;
                      window.open(href, "_blank", "noopener,noreferrer");
                    }}
                    className={cn(
                      "border-b border-gray-100 hover:bg-emerald-50/50 transition-colors",
                      index % 2 === 0 ? "bg-white" : "bg-gray-50/50",
                      onRowClick ? "cursor-pointer" : "",
                    )}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="py-4 px-4 text-sm">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              {hasFooter && (
                <tfoot>
                  {table.getFooterGroups().map((footerGroup) => (
                    <tr
                      key={footerGroup.id}
                      className="border-t border-gray-200 bg-gray-50/60"
                    >
                      {footerGroup.headers.map((header) => (
                        <td key={header.id} className="py-3 px-4 text-sm">
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.footer,
                                header.getContext(),
                              )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tfoot>
              )}
            </table>
        )}
      </div>
      {footerPagination && (
        <div className="flex shrink-0 justify-end border-t border-gray-200 px-6 py-4">
          {footerPagination}
        </div>
      )}
    </div>
  );
}
